import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Box, ScopedCssBaseline, styled, IconButton, Drawer, useMediaQuery, Fab, Typography, keyframes } from '@mui/material'
import { Chat as ChatIcon, Close as CloseIcon, Videocam as VideocamIcon, VideocamOff as VideocamOffIcon, ScreenRotation as RotateIcon } from '@mui/icons-material'
import MasterAppBar from '../master/components/MasterAppBar'
import { useAuthContext } from '../auth/hooks/useAuthContext';
import DrawingBoard from './components/DrawingBoard';
import Tabs from './components/Tabs';
import StudentBoard from './components/StudentBoard';
import VideoStream from './components/VideoStream';
import ChatArea from './components/ChatArea';
import { socket } from '../../config/SocketApi';
import { recordingSocket } from '../../config/SocketApi';
import { useParams } from 'react-router-dom'
import StudentVideoStream from './components/StudentVideoStream';
import ShareScreenHelper from './helpers/shareScreenHelper';
import SelectSreenShare from './components/SelectSreenShare';
import ButtomBar from './components/ButtomBar';
import { useNavigate } from 'react-router-dom';
import SelectMeeting from './components/SelectMeeting';
import { reset } from '../../features/tabs/tabsSlice';
import { toggleMeetingState } from '../../features/meeting/meetingSlice';
import { useDispatch } from 'react-redux';
import ActionCenter from '../shared/ActionCenter';
import StudentPoll from './components/StudentPoll';
import { useTranslation } from 'react-i18next';
import API from '../../config/API';

// Memoized without a custom comparator so orientation/layout changes still flow through.
const MemoizedStudentBoard = React.memo(StudentBoard);
const MemoizedStudentVideoStream = React.memo(StudentVideoStream);
const MemoizedChatArea = React.memo(ChatArea);

// MOBILE BREAKPOINT: 1000px - anything wider shows desktop/laptop mode
const MOBILE_BREAKPOINT = 1000;

// Rotate animation
const rotateAnimation = keyframes`
    0% { transform: rotate(0deg); }
    25% { transform: rotate(-15deg); }
    50% { transform: rotate(0deg); }
    75% { transform: rotate(15deg); }
    100% { transform: rotate(0deg); }
`;

const pulseAnimation = keyframes`
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(0.95); }
`;

// STYLED COMPONENTS
const WhiteBoardContainer = styled(Box)(({ theme }) => ({
    height: '100vh',
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#0f0f1a',
    background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
}));

const ClassRoomBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    flex: 1,
    width: '100%',
    gap: '8px',
    padding: '8px',
    minHeight: 0,
    // Ensure it takes the remaining height after the 60px header
    height: 'calc(100vh - 60px)',
    maxHeight: 'calc(100vh - 60px)',
}));

const RightBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    borderRadius: '12px',
    backgroundColor: 'rgba(26, 26, 46, 0.6)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const ButtonsBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
    height: '56px',
    flexShrink: 0,
    padding: '0 16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const LeftBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    width: '300px',
    minWidth: '300px',
    maxWidth: '300px',
    flexDirection: 'column',
    flexShrink: 0,
    gap: '8px',
    minHeight: 0,
}));

// Rotate Phone Overlay for Portrait Mobile
const RotateOverlay = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 15, 26, 0.98)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '24px',
    textAlign: 'center',
}));

const PhoneIcon = styled(Box)(({ theme }) => ({
    width: '80px',
    height: '140px',
    border: '4px solid #667eea',
    borderRadius: '16px',
    position: 'relative',
    marginBottom: '32px',
    animation: `${rotateAnimation} 2s ease-in-out infinite`,
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '8px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '20px',
        height: '4px',
        backgroundColor: '#667eea',
        borderRadius: '2px',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '8px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '24px',
        height: '24px',
        border: '3px solid #667eea',
        borderRadius: '50%',
    },
}));

const WhitBoard = () => {
    const dispatch = useDispatch()
    const { user } = useAuthContext() || JSON.parse(localStorage.getItem("user"));
    const { s_id, tu_id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    
    // Mobile detection - Changed breakpoint to 1000px
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    // Portrait detection for mobile
    const isPortrait = useMediaQuery('(orientation: portrait)');
    const showRotateOverlay = isMobile && isPortrait;
    const mobileLayoutKey = `${isMobile ? 'mobile' : 'desktop'}-${isPortrait ? 'portrait' : 'landscape'}`;
    
    // Mobile UI state
    const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
    const [showMobileVideo, setShowMobileVideo] = useState(true);
    const isDraggingRef = useRef(false);
    const dragStartRef = useRef({ x: 0, y: 0 });
    const positionRef = useRef({ x: 16, y: 100 });
    const endSessionInProgressRef = useRef(false);

    const finalizeSessionLocally = useCallback(() => {
        socket.emit("stop-room-recordings", { room_id: s_id });
        socket.emit("end-browser-page", { room_id: s_id });
        navigate('/');
    }, [s_id, navigate]);

    const handleEndSession = useCallback(async () => {
        if (endSessionInProgressRef.current) {
            return true;
        }

        endSessionInProgressRef.current = true;
        try {
            const response = await API.post(`session/status`, {
                session_id: s_id,
                new_status: 2,
            }, {
                headers: {
                    Authorization: user?.token,
                    "Content-Type": 'application/json'
                }
            });

            const ok = response?.status >= 200 && response?.status < 300 && response?.data?.status !== 'error';
            if (!ok) {
                throw new Error(response?.data?.message || 'Unable to update session status');
            }

            finalizeSessionLocally();
            return true;
        } catch (error) {
            console.log("[Classroom] Failed to end session:", error?.message || error);
            endSessionInProgressRef.current = false;
            return false;
        }
    }, [s_id, user?.token, finalizeSessionLocally]);
    
    // Mobile draggable video handlers
    const videoContainerRef = useRef(null);
    
    const handleDragStart = useCallback((e) => {
        if (!isMobile) return;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        isDraggingRef.current = true;
        dragStartRef.current = { x: clientX - positionRef.current.x, y: clientY - positionRef.current.y };
    }, [isMobile]);
    
    const handleDragEnd = useCallback(() => {
        isDraggingRef.current = false;
    }, []);
    
    // Add touch/mouse event listeners with passive: false
    useEffect(() => {
        const container = videoContainerRef.current;
        if (!container || !isMobile) return;
        
        const onTouchMove = (e) => {
            if (!isDraggingRef.current) return;
            e.preventDefault();
            const clientX = e.touches[0].clientX;
            const clientY = e.touches[0].clientY;
            const newX = Math.max(0, Math.min(window.innerWidth - 100, clientX - dragStartRef.current.x));
            const newY = Math.max(10, Math.min(window.innerHeight - 100, clientY - dragStartRef.current.y));
            positionRef.current = { x: newX, y: newY };
            container.style.left = `${newX}px`;
            container.style.top = `${newY}px`;
        };
        
        const onMouseMove = (e) => {
            if (!isDraggingRef.current) return;
            e.preventDefault();
            const newX = Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragStartRef.current.x));
            const newY = Math.max(10, Math.min(window.innerHeight - 100, e.clientY - dragStartRef.current.y));
            positionRef.current = { x: newX, y: newY };
            container.style.left = `${newX}px`;
            container.style.top = `${newY}px`;
        };
        
        container.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('mousemove', onMouseMove, { passive: false });
        
        return () => {
            container.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('mousemove', onMouseMove);
        };
    }, [isMobile]);
    
    useEffect(() => {
        if (!user) return
        if (user.user_type !== 0 && parseInt(tu_id) !== user.id) {
            socket.auth = { token: user?.token, room_id: s_id, user_type: 0, user_id: user?.id, user_name: user?.full_name, user_image: user?.img_path, isRecording: false }
            socket.connect()
            recordingSocket.auth = { token: user?.token, room_id: s_id, user_type: 0, user_id: user?.id, user_name: user?.full_name, user_image: user?.img_path, isRecording: true }
            recordingSocket.connect()
            recordingSocket.on("end-session", () => {
                if (user?.user_type !== 0) handleEndSession()
            })
        } else {
            socket.auth = { token: user?.token, room_id: s_id, user_type: user.user_type, user_id: user?.id, user_name: user?.full_name, user_image: user?.img_path, isRecording: false }
            socket.connect()
            recordingSocket.auth = { token: user?.token, room_id: s_id, user_type: user.user_type, user_id: user?.id, user_name: user?.full_name, user_image: user?.img_path, isRecording: true }
            recordingSocket.connect()
            recordingSocket.on("end-session", () => {
                if (user?.user_type !== 0) handleEndSession()
            })
        }
        return () => {
            dispatch(reset({}))
            dispatch(toggleMeetingState({ status: false }))
            recordingSocket.off("end-session");
            if (socket.connected) socket.disconnect();
            if (recordingSocket.connected) recordingSocket.disconnect()
        }

    }, [user, s_id, tu_id, dispatch, handleEndSession])

    // User type detection
    const userType = user ? Number(user.user_type) : -1;
    const tutorIdFromUrl = parseInt(tu_id);
    const currentUserId = parseInt(user?.id);
    const isTeacher = userType === 1 && tutorIdFromUrl === currentUserId;
    const isStudent = !isTeacher;

    // Mobile student layout with rotate overlay
    if (isMobile && isStudent) {
        // Show rotate phone overlay if in portrait mode
        if (showRotateOverlay) {
            return (
                <ScopedCssBaseline>
                    <RotateOverlay>
                        <PhoneIcon />
                        <Typography 
                            variant="h5" 
                            sx={{ 
                                color: '#fff', 
                                fontWeight: 700, 
                                mb: 2,
                                animation: `${pulseAnimation} 2s ease-in-out infinite`,
                            }}
                        >
                            {t('rotatePhone') || 'Please Rotate Your Phone'}
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: 'rgba(255,255,255,0.7)', 
                                maxWidth: 280,
                                lineHeight: 1.6,
                            }}
                        >
                            {t('rotatePhoneMessage') || 'For the best classroom experience, please rotate your device to landscape mode.'}
                        </Typography>
                        <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RotateIcon sx={{ color: '#667eea', fontSize: 28 }} />
                            <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 600 }}>
                                {t('landscape') || 'Landscape Mode'}
                            </Typography>
                        </Box>
                    </RotateOverlay>
                </ScopedCssBaseline>
            );
        }
        
        // Landscape mobile view
        return (
            <ScopedCssBaseline>
                <ActionCenter />
                {user && <WhiteBoardContainer>
                    {/* Full-screen whiteboard - NO header for full immersion */}
                    <Box sx={{ 
                        flex: 1, 
                        position: 'relative', 
                        overflow: 'hidden',
                        width: '100vw',
                        height: '100vh',
                    }}>
                        <MemoizedStudentBoard key={`student-board-${mobileLayoutKey}`} />
                        
                        {/* Floating chat FAB */}
                        <Fab
                            color="primary"
                            size="medium"
                            onClick={() => setChatDrawerOpen(true)}
                            sx={{
                                position: 'fixed',
                                bottom: 20,
                                right: 16,
                                bgcolor: 'rgba(102, 126, 234, 0.9)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { bgcolor: 'rgba(102, 126, 234, 1)' },
                                zIndex: 1000,
                            }}
                        >
                            <ChatIcon />
                        </Fab>
                        
                        {/* Video toggle FAB */}
                        <Fab
                            color="secondary"
                            size="small"
                            onClick={() => setShowMobileVideo(!showMobileVideo)}
                            sx={{
                                position: 'fixed',
                                bottom: 80,
                                right: 16,
                                bgcolor: showMobileVideo ? 'rgba(76, 175, 80, 0.9)' : 'rgba(244, 67, 54, 0.9)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': { bgcolor: showMobileVideo ? 'rgba(76, 175, 80, 1)' : 'rgba(244, 67, 54, 1)' },
                                zIndex: 1000,
                            }}
                        >
                            {showMobileVideo ? <VideocamIcon /> : <VideocamOffIcon />}
                        </Fab>
                        
                        {/* Draggable circular video overlay */}
                        <Box
                            ref={videoContainerRef}
                            onTouchStart={handleDragStart}
                            onTouchEnd={handleDragEnd}
                            onMouseDown={handleDragStart}
                            onMouseUp={handleDragEnd}
                            onMouseLeave={handleDragEnd}
                            sx={{
                                position: 'fixed',
                                left: positionRef.current.x,
                                top: positionRef.current.y,
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                overflow: 'hidden',
                                border: '3px solid rgba(102, 126, 234, 0.8)',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
                                zIndex: 999,
                                cursor: 'grab',
                                transition: 'box-shadow 0.3s ease, opacity 0.3s ease, transform 0.3s ease',
                                opacity: showMobileVideo ? 1 : 0,
                                pointerEvents: showMobileVideo ? 'auto' : 'none',
                                transform: showMobileVideo ? 'scale(1)' : 'scale(0.5)',
                                '&:hover': {
                                    boxShadow: '0 6px 25px rgba(102, 126, 234, 0.5)',
                                },
                                touchAction: 'none',
                            }}
                        >
                            <MemoizedStudentVideoStream key={`student-video-${mobileLayoutKey}`} isMobileOverlay={true} />
                        </Box>
                        
                        {/* Chat Drawer */}
                        <Drawer
                            anchor="right"
                            open={chatDrawerOpen}
                            onClose={() => setChatDrawerOpen(false)}
                            PaperProps={{
                                sx: {
                                    width: '85vw',
                                    maxWidth: 350,
                                    bgcolor: 'rgba(26, 26, 46, 0.98)',
                                    backdropFilter: 'blur(20px)',
                                }
                            }}
                        >
                            <IconButton 
                                onClick={() => setChatDrawerOpen(false)} 
                                sx={{ 
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    color: '#fff',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    zIndex: 10,
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                    }
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                            <Box sx={{ flex: 1, overflow: 'hidden', height: '100vh', pt: 1 }}>
                                {s_id && <MemoizedChatArea room_id={s_id} />}
                            </Box>
                        </Drawer>
                    </Box>
                    
                    <StudentPoll />
                </WhiteBoardContainer>}
            </ScopedCssBaseline>
        );
    }

    // Desktop layout (original)
    return (
        <ScopedCssBaseline>
            <ActionCenter />
            {user && <WhiteBoardContainer>
                <MasterAppBar crumb={false} virtualRoom={true} />
                <ClassRoomBox>
                    <RightBox>
                        {/* ONLY render teacher UI if isTeacher is true */}
                        {isTeacher && (
                            <>
                                <ButtonsBox>
                                    <SelectSreenShare room_id={s_id} />
                                    <SelectMeeting />
                                </ButtonsBox>
                                <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0 }}>
                                    <DrawingBoard />
                                    <Tabs />
                                    <ShareScreenHelper />
                                </Box>
                            </>
                        )}
                        {/* Student whiteboard - FILL AVAILABLE SPACE */}
                        {!isTeacher && (
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                <StudentBoard />
                            </Box>
                        )}
                    </RightBox>
                    <LeftBox>
                        {isTeacher ? <VideoStream /> : <StudentVideoStream />}
                        {s_id && <MemoizedChatArea room_id={s_id} />}
                    </LeftBox>
                </ClassRoomBox>
                {isTeacher && <ButtomBar room_id={s_id} onEndSession={handleEndSession} />}
                {!isTeacher && <StudentPoll />}
            </WhiteBoardContainer>}
        </ScopedCssBaseline>
    )
}

export default WhitBoard
